import './polyfills';
import './sentry';
import { createRoot } from 'react-dom/client';
import React from 'react';
import Keycloak from 'keycloak-js';
import ReactGA from 'react-ga4';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import './i18n';
import { http, initHttpClient } from './js/api/axios';
import Utils from './js/shared/utils';
import { initTogglz } from './js/shared/utils/togglz';
import { initCookie, loadSpocCookie, logout, saveTutorialCookie } from './js/store/authentication/actions';
import { appConfig } from './js/config';
import App from './js/shared/components/app';
import { initStore } from './js/configureStore';
import 'react-loading-skeleton/dist/skeleton.css';
import './css/style.scss';
// gild-loader.scss intentionally imported after `style.scss` due to its use
// of `@use` within scss. we want gild styles to be output after base styles,
// but scss requires any use of `@use` to come before any `@import` within
// any given single scss file.
import './css/gild-loader.scss';
import { log } from './js/shared/utils/log';

const store = initStore();

const kc = new Keycloak('/keycloak.json');

initHttpClient(kc, store);

// ************************************
// keycloak initialization
// ************************************

const token = localStorage.getItem('token');
const refreshToken = localStorage.getItem('refreshToken');

kc.init({ onLoad: 'check-sso', checkLoginIframe: false, token, refreshToken })
    .then(authenticated => {
        if (authenticated) {
            store.getState().authentication.kc = kc;

            localStorage.setItem('token', kc.token);
            localStorage.setItem('refreshToken', kc.refreshToken);

            setInterval(() => {
                kc.updateToken(300)
                    .then(refreshed => {
                        if (refreshed) {
                            localStorage.setItem('token', kc.token);
                            localStorage.setItem('refreshToken', kc.refreshToken);
                        }
                    })
                    .catch(error => {
                        log.error(error ?? 'Unable to refresh token');
                        logout(kc);
                    });
            }, 30000);

            initCookie();

            if (!isNil(kc) && !isNil(kc.tokenParsed)) {
                const tokenParsed = kc.tokenParsed;
                if (isNil(tokenParsed.partnerId) && isNil(tokenParsed.siteIds) && !Utils.isUserAdmin({ kc })) {
                    const msg =
                        'No context found for your user\n\n' +
                        'Please inform your system administrator about this problem!';
                    logout(kc);
                    alert(msg);
                    return;
                }
            }

            Promise.all([http.get(appConfig().endpointSpocs), http.get(appConfig().endpointTutorial)])
                .then(([spocs, tutorial]) => {
                    store.getState().authentication.spocs = spocs.data;
                    spocState(store.getState().authentication);
                    saveTutorialCookie(tutorial.data);

                    return Promise.all([initServices(), http.get(appConfig().endpointTogglz)]);
                })
                .then(([ignored, togglz]) => {
                    initTogglz(togglz.data);
                    const container = document.getElementById('app');
                    const root = createRoot(container);
                    root.render(<App store={store} />);
                })
                .catch(_ => {
                    const container = document.getElementById('app');
                    const root = createRoot(container);
                    root.render(<App store={store} maintenance={true} />);
                });
        } else {
            // show possibly some other page here...
            kc.login();
        }
    })
    .catch(error => log.error(error ?? 'authentication failed'));

const spocState = authentication => {
    const restoredSpoc = loadSpocCookie();
    const spocID = restoredSpoc ? restoredSpoc.id : null;
    let futureSpoc = null;
    let allowed = false;
    if (spocID && authentication.spocs) {
        authentication.spocs.some(partner => {
            if (isEqual(spocID, partner.id)) {
                allowed = true;
                futureSpoc = partner;
                return true;
            }

            if (partner.sites) {
                partner.sites.some(site => {
                    if (isEqual(spocID, site.id)) {
                        allowed = true;
                        futureSpoc = site;
                        return true;
                    }

                    return false;
                });
                if (!isNil(futureSpoc)) return true;
            }

            return false;
        });
    }

    if (!futureSpoc || !allowed) {
        const token = authentication.kc.tokenParsed;
        if (token.partnerId) {
            futureSpoc = find(authentication.spocs, {
                id: `PARTNER${token.partnerId}`
            });
        }
        if (!futureSpoc) {
            futureSpoc = authentication.spocs[0];
        }
        if (futureSpoc.sites && futureSpoc.sites[0]) {
            futureSpoc = futureSpoc.sites[0];
        }
    }

    authentication.spoc = futureSpoc || {};
};

// ************************************
// eFor cypress interception to get campaign products
// ************************************

if (window.Cypress) {
    window.store = store;
}

// ************************************
// external stage configuration
// ************************************

const initServices = () =>
    http
        .get('/config.json', { external: true })
        .then(response => {
            const config = response.data;
            store.getState().configs = config;
            ReactGA.initialize(config.gaMeasurementID);
            const isAdminUser = Utils.isUserAdmin(store.getState().authentication);
            ReactGA.ga('set', 'dimension1', isAdminUser ? 'internal' : 'external');
            ReactGA.ga('send', 'pageview', { dimension1: isAdminUser ? 'internal' : 'external' });
        })
        .catch(err => {
            throw new Error(`Could not initialize services. Error: ${err.message}`);
        });

export default store;
